import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { ConnectedComboBox } from '@eti/form';
import { useProperty, useSnowplowTracking } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { constants, persistInputData } from '@eti/utils';
import type { MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import type { FormStateMap } from 'redux-form';
import { formValueSelector } from 'redux-form';
import type { Country } from '../../models';
import type { PhoneType } from './Phone';

const { MOUSE_FLOW_CLASS_NAMES } = constants;

const getComboBoxStyles = (prefix: string) => css`
  & label {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  & input:not([hidden]) {
    grid-area: 1 / 2;
  }

  & div:has(> input:not([hidden])) {
    display: inline-grid;

    &::after {
      content: '+${prefix}';
      font-size: 1rem;
      grid-area: 1 / 2;
      padding: 0.375rem 0.75rem;
      padding-inline-end: 2.6875rem;
      visibility: hidden;
      white-space: nowrap;
      width: auto;
    }
  }
`;

const getOptions = (countries: Country[]) =>
  countries.map((country: Country) => ({
    ...country,
    label: `${country.value} (+${country.phoneCountryPrefix})`,
    value: String(country.id),
  }));

type Option = ReturnType<typeof getOptions>[number];

const normalize = (option: Option) => Number(option?.value) ?? '';

interface ConnectedProps {
  associatedPhoneInput: PhoneType;
  countries: Country[];
  form: string;
  formSection: string;
  label: string;
  name: string;
  onCountryChange?: (phoneType: PhoneType) => void;
}

interface ConnectedState {
  selectedPrefix?: string;
}

const mapStateToProps: MapStateToProps<ConnectedState, ConnectedProps, { form: FormStateMap }> = (
  state,
  { countries, form, formSection, name },
) => {
  const selectedOption = formValueSelector(form)(state, `${formSection}.${name}`);
  const selectedPrefix = countries.find((country) => country.id === selectedOption)
    ?.phoneCountryPrefix;

  return {
    selectedPrefix,
  };
};

interface PhoneCountryDropdownProps extends ConnectedState, ConnectedProps {}

const PhoneCountryCodeInput = ({
  associatedPhoneInput,
  countries,
  form,
  formSection,
  label,
  name,
  onCountryChange,
  selectedPrefix,
}: PhoneCountryDropdownProps) => {
  const { p } = useProperty();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const [prefix, setPrefix] = useState(() => selectedPrefix ?? '');

  useEffect(() => {
    const eventListener = (event: KeyboardEvent) => {
      if ((event.key === 'Escape' || event.key === 'Tab') && selectedPrefix) {
        setPrefix(selectedPrefix);
      }
    };

    window.addEventListener('keydown', eventListener, true);

    return () => {
      window.removeEventListener('keydown', eventListener);
    };
  }, [selectedPrefix]);

  const options = getOptions(countries);
  const format = (storedOption: number) =>
    options.find((option) => Number(option.value) === storedOption) ?? '';

  const formatSelectedOption = (country: Option) => {
    const s = options.find((c) => c.value === country.value);
    return s ? `+${s?.phoneCountryPrefix}` : '';
  };

  const handleOnInput = (event: ChangeEvent<HTMLInputElement>) => {
    setPrefix(event.target.value);
  };

  const handleOnBlur = (option: Option) => {
    setPrefix(option.phoneCountryPrefix);
  };

  const handleOnChange = (option: Option) => {
    if (p('IbeClient.TravelerDetails.PersistFormData.Enabled')) {
      persistInputData(form, name)(option.id);
    }

    setPrefix(option.phoneCountryPrefix);

    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Traveler Details',
        action: 'Select',
        label: 'Phone country code',
        property: option.code,
      });
    }

    if (onCountryChange) {
      onCountryChange(associatedPhoneInput);
    }
  };

  return (
    <ConnectedComboBox
      className={cx(getComboBoxStyles(prefix), MOUSE_FLOW_CLASS_NAMES)}
      data-testid={`travelerDetails-${formSection}-${name}-combobox`}
      format={format}
      formatSelectedOption={formatSelectedOption}
      label={label}
      name={name}
      normalize={normalize}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      onInput={handleOnInput}
      options={options}
      size={1}
    />
  );
};

export default connect(mapStateToProps)(PhoneCountryCodeInput);
